import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { ajaxGetBoms, ajaxSaveBom, ajaxDeleteBom } from '../../services/bomService';
import { ajaxGetPartComponents } from '../../services/partComponentService';
import ConfirmationButton from '../../components/ConfirmationButton';

export default function ProductBomDialog({ open, onClose, onSave, bom, productId }) {
  const [formData, setFormData] = useState({
    id: null,
    product_id: productId,
    part_id: '',
    quantity: '',
    ref: '',
    schematic: '',
    remark: '',
    alt_parts: []
  });

  const [parts, setParts] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (bom) {
      // Load BOM data when in edit mode
      ajaxGetBoms({ BomId: bom.id })
        .then((res) => {
          const bomData = res.data;
          setFormData({
            id: bomData.id,
            product_id: bomData.product_id,
            part_id: bomData.part_id,
            quantity: bomData.quantity,
            ref: bomData.ref || '',
            schematic: bomData.schematic || '',
            remark: bomData.remark || '',
            alt_parts: bomData.alt_parts?.map(alt => alt.part_id) || []
          });
        })
        .catch(() => {
          setFormData({
            id: null,
            product_id: productId,
            part_id: '',
            quantity: '',
            ref: '',
            schematic: '',
            remark: '',
            alt_parts: []
          });
        });
    } else {
      setFormData({
        id: null,
        product_id: productId,
        part_id: '',
        quantity: '',
        ref: '',
        schematic: '',
        remark: '',
        alt_parts: []
      });
    }
  }, [bom, productId]);

  useEffect(() => {
    ajaxGetPartComponents()
      .then((res) => {
        setParts(res.data || []);
      })
      .catch(() => {
        setParts([]);
      });
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      part_id: value ? value.id : ''
    }));
  };

  const handleAltPartsChange = (event, values) => {
    setFormData(prev => ({
      ...prev,
      alt_parts: values.map(part => part.id)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.part_id) newErrors.part_id = 'Manufacturer Part is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (formData.quantity && isNaN(formData.quantity)) newErrors.quantity = 'Quantity must be a number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      ajaxSaveBom(formData)
        .then(() => {
          onSave();
        })
        .catch((error) => {
          console.error('Error saving BOM:', error);
        });
    }
  };

  const handleDelete = () => {
    ajaxDeleteBom({ BomId: bom.id })
      .then(() => {
        onSave();
      })
      .catch((error) => {
        console.error('Error deleting BOM:', error);
      });
  };

  const selectedPart = parts.find(part => part.id === formData.part_id) || null;
  const selectedAltParts = parts.filter(part => formData.alt_parts.includes(part.id));

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>{bom ? 'Edit BOM' : 'Add BOM'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              options={parts}
              value={selectedPart}
              onChange={handlePartChange}
              getOptionLabel={(option) => `[${option.manuf_part_number}] ${option.description}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manufacturer Part"
                  error={!!errors.part_id}
                  helperText={errors.part_id}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="quantity"
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="ref"
              label="Reference"
              value={formData.ref}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="schematic"
              label="Part in Schematic"
              value={formData.schematic}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              name="remark"
              label="Remark"
              value={formData.remark}
              onChange={handleChange}
              multiline
              minRows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              multiple
              disableCloseOnSelect
              options={parts}
              value={selectedAltParts}
              onChange={handleAltPartsChange}
              getOptionLabel={(option) => `[${option.manuf_part_number}] ${option.description}`}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {`[${option.manuf_part_number}] ${option.description}`}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.manuf_part_number}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Alternative Parts"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {bom && (
          <ConfirmationButton
            buttonProps={{
              variant: 'contained',
              color: 'error',
              sx: { mr: 'auto' }
            }}
            handleDialogClickYes={handleDelete}
            buttonText="Delete"
            dialogText="Are you sure you want to delete this BOM?"
            dialogYesText="Confirm"
            dialogNoText="Cancel"
          />
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {bom ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
